
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import * as bookingService from '../services/bookingService';
import type { Hotel, Offer, SearchQuery, Passenger, ContactDetails, BookingRequest, ApiLogEntry, BrandedFare } from '../types';

interface BookingPageProps {
    logApiCall: (log: ApiLogEntry) => void;
}

export const BookingPage: React.FC<BookingPageProps> = ({ logApiCall }) => {
    const { t, culture, currency: contextCurrency, nationality: contextNationality } = useLocalization();
    const navigate = useNavigate();
    const { state } = useLocation();

    const hotel = state?.hotel as Hotel;
    const offer = state?.offer as Offer;
    const searchQuery = state?.searchQuery as SearchQuery;

    const [passengers, setPassengers] = useState<Passenger[]>([]);
    const [contact, setContact] = useState<ContactDetails>({
        email: '', phone: '', address: '', city: '', zipCode: '', country: 'Sweden'
    });

    // Booking session state
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [transactionLoading, setTransactionLoading] = useState(false);
    const [transactionError, setTransactionError] = useState<string | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<number>(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reservationData, setReservationData] = useState<any>(null);
    // Flight selection price delta — updated when user picks alternative flight
    const [priceDelta, setPriceDelta] = useState(0);

    // Branded fares state
    const [outboundBrandedFares, setOutboundBrandedFares] = useState<BrandedFare[]>([]);
    const [returnBrandedFares, setReturnBrandedFares] = useState<BrandedFare[]>([]);
    const [selectedOutboundFare, setSelectedOutboundFare] = useState<string | null>(null);
    const [selectedReturnFare, setSelectedReturnFare] = useState<string | null>(null);
    const [brandedFaresLoading, setBrandedFaresLoading] = useState(false);
    const [brandedFareDelta, setBrandedFareDelta] = useState(0);

    // Baggage state
    const [baggageOptions, setBaggageOptions] = useState<any[]>([]);
    const [selectedBaggage, setSelectedBaggage] = useState<Record<string, string>>({});
    const [baggageDelta, setBaggageDelta] = useState(0);
    const [baggageLoading, setBaggageLoading] = useState(false);

    // Initialize booking transaction on mount
    const initializeTransaction = useCallback(async () => {
        if (!offer?.id) return;

        setTransactionLoading(true);
        setTransactionError(null);

        try {
            // Build flight data from the offer to persist in the booking
            const flightData = (offer.outboundFlight || offer.returnFlight) ? {
                outboundFlight: offer.outboundFlight || null,
                returnFlight: offer.returnFlight || null
            } : undefined;

            // Tourvisio beginTransaction only accepts the base package offerId.
            // Branded fares & alternative flights are already encoded within the offer ID string.
            // Sending extra IDs causes "General Exception" from Tourvisio.
            const session = await bookingService.startBooking([offer.id], contextCurrency, flightData, culture);
            setSessionId(session.sessionId);
            if (session.reservationData) {
                setReservationData(session.reservationData);
                console.log('📋 ReservationData received:', JSON.stringify(session.reservationData, null, 2));

                // Verify price changes from reservationData vs cached offer price
                const currentPrice = session.reservationData.price?.amount || offer.price;
                const cachedPrice = offer.price;
                const currentCurrency = session.reservationData.price?.currency || offer.currency;
                
                if (Math.abs(currentPrice - cachedPrice) > 0.01) {
                    const priceDifference = currentPrice - cachedPrice;
                    const priceIncreased = priceDifference > 0;
                    setPriceWarning({
                        show: true,
                        message: priceIncreased 
                            ? `Fiyat ${Math.abs(priceDifference).toFixed(2)} ${currentCurrency} arttı` 
                            : `Fiyat ${Math.abs(priceDifference).toFixed(2)} ${currentCurrency} azaldı`,
                        currentPrice: currentPrice,
                        priceChanged: true,
                        priceIncreased: priceIncreased
                    });
                }
            }
            console.log('✅ Booking session started/kontrol completed:', session.sessionId);
        } catch (error) {
            console.error('Failed to start booking transaction:', error);
            setTransactionError(error instanceof Error ? error.message : 'Failed to start booking');
        } finally {
            setTransactionLoading(false);
            setVerifyingPrice(false); // Enable the button after control
        }
    }, [offer]);

    useEffect(() => {
        if (!hotel || !offer || !searchQuery) {
            navigate('/');
            return;
        }

        // Initialize empty passenger forms based on search query
        const initialPassengers: Passenger[] = [];
        let idCounter = 1;
        for (let i = 0; i < searchQuery.adults; i++) {
            initialPassengers.push({
                id: idCounter++,
                type: 'Adult',
                firstName: '',
                lastName: '',
                birthDate: '',
                gender: 'Male',
                nationality: contextNationality
            });
        }

        for (let i = 0; i < searchQuery.children; i++) {
            initialPassengers.push({
                id: idCounter++,
                type: 'Child',
                firstName: '',
                lastName: '',
                birthDate: '',
                gender: 'Male',
                nationality: contextNationality
            });
        }

        setPassengers(initialPassengers);

        // Start booking transaction
        initializeTransaction();

        // Fetch branded fares
        const fetchBrandedFares = async () => {
            if (!offer?.id) return;
            setBrandedFaresLoading(true);
            try {
                const details = await bookingService.getOfferDetails(offer.id, contextCurrency, culture);
                if (details.outboundBrandedFares?.length > 0) {
                    setOutboundBrandedFares(details.outboundBrandedFares);
                    // Select default fare
                    const defaultFare = details.outboundBrandedFares.find(f => f.isDefault);
                    if (defaultFare) setSelectedOutboundFare(defaultFare.id);
                }
                if (details.returnBrandedFares?.length > 0) {
                    setReturnBrandedFares(details.returnBrandedFares);
                    const defaultFare = details.returnBrandedFares.find(f => f.isDefault);
                    if (defaultFare) setSelectedReturnFare(defaultFare.id);
                }
                console.log('✈️ Branded fares loaded:', details.outboundBrandedFares?.length, 'outbound,', details.returnBrandedFares?.length, 'return');
            } catch (err) {
                console.error('Failed to load branded fares:', err);
            } finally {
                setBrandedFaresLoading(false);
            }
        };
        fetchBrandedFares();
    }, [hotel, offer, searchQuery, navigate, initializeTransaction]);

    // Poll transaction details for timer
    useEffect(() => {
        if (!sessionId) return;

        let intervalId: ReturnType<typeof setInterval> | null = null;

        const updateTimer = async () => {
            try {
                const details = await bookingService.getTransactionDetails(sessionId);
                setTimeRemaining(details.timeRemainingMs);

                if (details.isExpired) {
                    setTransactionError('Session expired. Please start a new booking.');
                    if (intervalId) clearInterval(intervalId);
                }
            } catch (error) {
                // Stop polling if session is gone (404) — prevents console spam
                console.warn('Transaction details unavailable, stopping poll.');
                if (intervalId) clearInterval(intervalId);
            }
        };

        updateTimer();
        intervalId = setInterval(updateTimer, 30000); // Update every 30 seconds

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [sessionId]);

    // Fetch baggage options when session is ready
    useEffect(() => {
        if (!sessionId) return;
        const fetchBaggage = async () => {
            setBaggageLoading(true);
            try {
                const data = await bookingService.getBaggageOptions(sessionId);
                if (data.baggageOptions && data.baggageOptions.length > 0) {
                    setBaggageOptions(data.baggageOptions);
                    // Set default selected baggage (isIncluded: true)
                    const defaults: Record<string, string> = {};
                    data.baggageOptions.forEach((paxOpts: any) => {
                        const defaultOpt = paxOpts.options.find((o: any) => o.isIncluded);
                        if (defaultOpt) {
                            defaults[`${paxOpts.passengerId}-${paxOpts.direction}`] = defaultOpt.offerId;
                        }
                    });
                    setSelectedBaggage(defaults);
                }
            } catch (err) {
                console.error('Failed to fetch baggage options:', err);
            } finally {
                setBaggageLoading(false);
            }
        };
        fetchBaggage();
    }, [sessionId]);

    // Handle branded fare selection
    const handleFareSelect = (direction: 'outbound' | 'return', fareId: string) => {
        const fares = direction === 'outbound' ? outboundBrandedFares : returnBrandedFares;
        const selectedFare = fares.find(f => f.id === fareId);
        const previousFare = fares.find(f => f.id === (direction === 'outbound' ? selectedOutboundFare : selectedReturnFare));

        if (direction === 'outbound') {
            setSelectedOutboundFare(fareId);
        } else {
            setSelectedReturnFare(fareId);
        }

        // Calculate price delta from branded fare changes
        const previousPrice = previousFare?.price || 0;
        const newPrice = selectedFare?.price || 0;
        const delta = newPrice - previousPrice;
        setBrandedFareDelta(prev => prev + delta);
    };

    const formatBaggageName = (name: string): string => {
        if (!name) return '';
        let formatted = name;
        
        // Translate "Dahil" (Turkish for Included) to current language
        if (formatted.includes('(Dahil)')) {
            const replacement = culture === 'sv-SE' || culture === 'sv' ? '(Ingår)' : culture === 'da-DK' || culture === 'da' ? '(Inkluderet)' : '(Included)';
            formatted = formatted.replace('(Dahil)', replacement);
        } else if (formatted.includes('Dahil')) {
            const replacement = culture === 'sv-SE' || culture === 'sv' ? 'Ingår' : culture === 'da-DK' || culture === 'da' ? 'Inkluderet' : 'Included';
            formatted = formatted.replace('Dahil', replacement);
        }

        // Translate "Ekstra" (Swedish/Danish for Extra) to English if current language is English
        if (culture.startsWith('en')) {
            formatted = formatted.replace(/ekstra/gi, 'Extra');
        } else if (culture.startsWith('sv')) {
            // Also translate English "Extra" to Swedish "Ekstra" or "Extra"
            formatted = formatted.replace(/extra/gi, 'Ekstra');
        } else if (culture.startsWith('da')) {
            formatted = formatted.replace(/extra/gi, 'Ekstra');
        }
        
        // Translate "Included" (English) to Swedish/Danish if current language is Swedish/Danish
        if (culture === 'sv-SE' || culture === 'sv') {
            formatted = formatted.replace('(Included)', '(Ingår)').replace('Included', 'Ingår');
        } else if (culture === 'da-DK' || culture === 'da') {
            formatted = formatted.replace('(Included)', '(Inkluderet)').replace('Included', 'Inkluderet');
        }
        
        return formatted;
    };

    const formatErrorMessage = (errorMsg: string | null): string => {
        if (!errorMsg) return '';
        
        // Handle TIMEOUT error message
        if (errorMsg.toUpperCase().includes('TIMEOUT')) {
            return t('error_timeout') || (
                culture === 'sv-SE' || culture === 'sv' 
                    ? 'Begäran tog för lång tid. Vänligen försök igen.' 
                    : culture === 'da-DK' || culture === 'da'
                        ? 'Forbindelsen blev afbrudt. Prøv venligst igen.'
                        : 'Connection timed out. Please try again.'
            );
        }
        
        // Handle Session Expired error
        if (errorMsg.includes('Session expired') || errorMsg.includes('SESSION_EXPIRED')) {
            return t('session_expired_message') || (
                culture === 'sv-SE' || culture === 'sv'
                    ? 'Din session har löpt ut. Vänligen starta en ny sökning.'
                    : culture === 'da-DK' || culture === 'da'
                        ? 'Din session er udløbet. Start venligst en ny søgning.'
                        : 'Your session has expired. Please start a new search.'
            );
        }
        
        // Return original if no mapping exists
        return errorMsg;
    };

    const handleBaggageSelect = async (passengerId: string, direction: string, offerId: string) => {
        const key = `${passengerId}-${direction}`;
        const newSelected = { ...selectedBaggage, [key]: offerId };
        setSelectedBaggage(newSelected);

        if (!sessionId) return;
        
        // Call backend to update
        try {
            const selections = Object.entries(newSelected).map(([k, offId]) => ({
                passengerId: k.split('-')[0],
                offerId: offId as string
            }));
            const data = await bookingService.updateBaggage(sessionId, selections);
            if (data.success) {
                setBaggageDelta(data.baggageDelta);
            }
        } catch (err) {
            console.error('Failed to update baggage selection:', err);
        }
    };

    const handlePassengerChange = (index: number, field: keyof Passenger, value: string) => {
        const updated = [...passengers];
        updated[index] = { ...updated[index], [field]: value };
        setPassengers(updated);
    };

    const handleContactChange = (field: keyof ContactDetails, value: string) => {
        setContact({ ...contact, [field]: value });
    };

    const [verifyingPrice, setVerifyingPrice] = useState(false);
    const [priceWarning, setPriceWarning] = useState<{
        show: boolean;
        message: string;
        currentPrice: number;
        priceChanged: boolean;
        priceIncreased: boolean;
    } | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Basic validation
        if (passengers.some(p => !p.firstName || !p.lastName || !p.birthDate)) {
            alert('Please fill in all passenger details.');
            return;
        }
        if (!contact.email || !contact.phone) {
            alert('Please fill in contact details.');
            return;
        }

        // Check if session exists
        if (!sessionId) {
            setTransactionError('No active booking session. Please refresh and try again.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Price is already verified on page load. Proceed directly.
            // Set passengers via booking service
            const formattedPassengers = passengers.map((p, idx) => ({
                id: idx + 1,
                type: p.type as 'Adult' | 'Child' | 'Infant',
                title: p.gender === 'Female' ? 'Mrs' as const : 'Mr' as const,
                firstName: p.firstName,
                lastName: p.lastName,
                birthDate: p.birthDate,
                gender: p.gender as 'Male' | 'Female',
                nationality: p.nationality,
                passportNumber: p.passportNumber || ''
            }));

            const formattedContact = {
                firstName: passengers[0]?.firstName || '',
                lastName: passengers[0]?.lastName || '',
                email: contact.email,
                phone: contact.phone,
                address: contact.address || '',
                city: contact.city || '',
                zipCode: contact.zipCode || '',
                country: contact.country || 'Sweden',
                countryCode: '46',  // Phone country code (numeric), NOT ISO country code
                birthDate: passengers[0]?.birthDate || '',
                gender: passengers[0]?.gender || 'Male',
                nationality: contextNationality
            };

            await bookingService.setPassengers(sessionId, formattedPassengers, formattedContact);
            console.log('✅ Passengers set successfully');

            setVerifyingPrice(false);
            proceedToPayment();

        } catch (error) {
            console.error('Failed to set passengers:', error);
            setTransactionError(error instanceof Error ? error.message : 'Failed to process booking');
            setVerifyingPrice(false);
            setIsSubmitting(false);
        }
    };

    const proceedToPayment = () => {
        const bookingRequest: BookingRequest = {
            hotel,
            offer: priceWarning?.currentPrice ? { ...offer, price: priceWarning.currentPrice } : offer,
            searchQuery,
            passengers,
            contact
        };
        setPriceWarning(null);
        setIsSubmitting(false);
        // Pass sessionId to payment page for transaction completion
        navigate('/payment', { state: { bookingRequest, sessionId } });
    };

    if (!hotel || !offer) return null;

    // Safe date parsing — offer.checkIn may be undefined from deep-link navigation
    const rawCheckIn = offer.checkIn || searchQuery?.checkIn || new Date().toISOString().split('T')[0];
    const checkInParsed = new Date(rawCheckIn);
    const checkInDate = !isNaN(checkInParsed.getTime()) ? checkInParsed.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
    const nightCount = Number(offer.night || searchQuery?.nights || 7) || 7;
    let checkoutDate = checkInDate;
    try {
        const co = new Date(checkInDate);
        co.setDate(co.getDate() + nightCount);
        if (isNaN(co.getTime())) throw new Error("Invalid checkout date");
        checkoutDate = co.toISOString().split('T')[0];
    } catch { 
        checkoutDate = checkInDate;
    }

    // Extract travellers from reservationData for per-person breakdown
    const travellers = reservationData?.travellers || [];

    // Extract services from reservationData
    const services = reservationData?.services || [];

    // ──────────────────────────────────────────────────────────
    // Parse TourVisio API reservationData for sidebar display
    // ──────────────────────────────────────────────────────────
    const hotelService = services.find((s: any) => s.productType === 2);
    const flightService = services.find((s: any) => s.productType === 3);

    // Hotel details from API (fallback to local offer data)
    const apiHotelName = hotelService?.name || hotel.name;
    const apiCheckIn = hotelService?.beginDate?.split('T')[0] || checkInDate;
    const apiCheckOut = hotelService?.endDate?.split('T')[0] || checkoutDate;
    const apiNights = hotelService?.night || offer.night;
    const hotelRoom = hotelService?.rooms?.[0];
    const apiRoomName = hotelRoom?.roomName || hotelRoom?.name || offer.roomType;
    const apiBoardName = hotelRoom?.boardName || hotelRoom?.board?.name || offer.board;
    const apiCheckInTime = hotelRoom?.checkIn || hotelService?.checkIn || '14:00';
    const apiCheckOutTime = hotelRoom?.checkOut || hotelService?.checkOut || '12:00';
    const apiTravellersInRoom = hotelRoom?.travellers?.length || (searchQuery?.adults || 0) + (searchQuery?.children || 0);

    // Flight details from API — TourVisio uses segments with route: 1 (outbound) / 2 (return)
    const flightSegments = flightService?.segments || flightService?.flightSegments || [];
    const apiOutboundSegments = flightSegments.filter((s: any) => s.route === 1);
    const apiReturnSegments = flightSegments.filter((s: any) => s.route === 2);

    // Build outbound flight string from API segments (fallback to offer data)
    const apiOutbound = apiOutboundSegments.length > 0 ? {
        departureTime: apiOutboundSegments[0]?.departureTime?.substring(0, 5) || apiOutboundSegments[0]?.departureTime || '',
        departureAirport: apiOutboundSegments[0]?.departureAirport || apiOutboundSegments[0]?.from?.code || '',
        arrivalAirport: apiOutboundSegments[apiOutboundSegments.length - 1]?.arrivalAirport || apiOutboundSegments[apiOutboundSegments.length - 1]?.to?.code || '',
        flightNumber: apiOutboundSegments.map((s: any) => s.flightNumber || s.flightNo || `${s.airline || ''}${s.flightNumber || ''}`).filter(Boolean).join(', '),
        flightDate: apiOutboundSegments[0]?.departureDate || apiOutboundSegments[0]?.date || apiCheckIn,
    } : offer.outboundFlight ? {
        departureTime: offer.outboundFlight.departureTime,
        departureAirport: offer.outboundFlight.departureAirport,
        arrivalAirport: offer.outboundFlight.arrivalAirport,
        flightNumber: offer.outboundFlight.flightNumber,
        flightDate: checkInDate,
    } : null;

    const apiReturn = apiReturnSegments.length > 0 ? {
        departureTime: apiReturnSegments[0]?.departureTime?.substring(0, 5) || apiReturnSegments[0]?.departureTime || '',
        departureAirport: apiReturnSegments[0]?.departureAirport || apiReturnSegments[0]?.from?.code || '',
        arrivalAirport: apiReturnSegments[apiReturnSegments.length - 1]?.arrivalAirport || apiReturnSegments[apiReturnSegments.length - 1]?.to?.code || '',
        flightNumber: apiReturnSegments.map((s: any) => s.flightNumber || s.flightNo || `${s.airline || ''}${s.flightNumber || ''}`).filter(Boolean).join(', '),
        flightDate: apiReturnSegments[0]?.departureDate || apiReturnSegments[0]?.date || apiCheckOut,
    } : offer.returnFlight ? {
        departureTime: offer.returnFlight.departureTime,
        departureAirport: offer.returnFlight.departureAirport,
        arrivalAirport: offer.returnFlight.arrivalAirport,
        flightNumber: offer.returnFlight.flightNumber,
        flightDate: checkoutDate,
    } : null;

    // Pricing from API (fallback to offer data)
    const apiTotalPrice = reservationData?.price?.amount
        || (hotelService?.price?.amount && flightService?.price?.amount
            ? hotelService.price.amount + flightService.price.amount
            : hotelService?.price?.amount)
        || (offer.price + priceDelta + brandedFareDelta + baggageDelta);
    const apiCurrency = reservationData?.price?.currency || hotelService?.price?.currency || offer.currency;

    // Determine price per person
    const totalPassengers = passengers.length || travellers.length || 1;
    const pricePerPerson = apiTotalPrice / totalPassengers;

    // Date formatting helper
    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
        return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    };

    return (
        <div className="min-h-screen py-8" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                <button onClick={() => navigate(-1)} className="mb-6 flex items-center text-gray-500 hover:text-gray-900 font-medium transition-colors text-sm group">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
                    {t('back_to_hotel')}
                </button>

                {/* Page Title */}
                <div className="mb-8">
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">{t('booking_overview')}</h1>
                    <p className="text-gray-500 mt-1">{t('complete_booking_msg')}</p>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* LEFT: Forms */}
                    <div className="lg:w-2/3 space-y-6">

                        {/* Passenger Forms */}
                        <form id="bookingForm" onSubmit={handleSubmit} className="space-y-6">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 text-white flex items-center justify-center text-sm font-bold shadow-sm">1</span>
                                    <h2 className="text-lg font-bold text-gray-900">{t('passenger_details')}</h2>
                                </div>

                                <div className="p-6 space-y-5">
                                    {passengers.map((p, index) => (
                                        <div key={p.id} className="p-5 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200/70">
                                            <div className="flex items-center gap-2 mb-4">
                                                <span className="text-lg">{p.type === 'Adult' ? '👤' : '👶'}</span>
                                                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">
                                                    {p.type === 'Adult' ? t('adult') : t('child')} {index + 1}
                                                </h3>
                                                {index === 0 && (
                                                    <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2.5 py-0.5 rounded-full font-semibold">
                                                        {t('lead_passenger')}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('gender')}</label>
                                                    <select
                                                        value={p.gender}
                                                        onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                                                        className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                    >
                                                        <option value="Male">{t('male')}</option>
                                                        <option value="Female">{t('female')}</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('first_name')}</label>
                                                    <input
                                                        type="text"
                                                        value={p.firstName}
                                                        onChange={(e) => handlePassengerChange(index, 'firstName', e.target.value)}
                                                        className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                        placeholder="John"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('last_name')}</label>
                                                    <input
                                                        type="text"
                                                        value={p.lastName}
                                                        onChange={(e) => handlePassengerChange(index, 'lastName', e.target.value)}
                                                        className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                        placeholder="Doe"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('birth_date')}</label>
                                                    <input
                                                        type="date"
                                                        value={p.birthDate}
                                                        onChange={(e) => handlePassengerChange(index, 'birthDate', e.target.value)}
                                                        className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('passport_number') || 'Passnummer'}</label>
                                                    <input
                                                        type="text"
                                                        value={p.passportNumber || ''}
                                                        onChange={(e) => handlePassengerChange(index, 'passportNumber', e.target.value)}
                                                        className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                        placeholder="AB1234567"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                                    <span className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 text-white flex items-center justify-center text-sm font-bold shadow-sm">2</span>
                                    <h2 className="text-lg font-bold text-gray-900">{t('contact_details')}</h2>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('email')}</label>
                                            <input
                                                type="email"
                                                value={contact.email}
                                                onChange={(e) => handleContactChange('email', e.target.value)}
                                                className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                placeholder="john.doe@email.com"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('phone')}</label>
                                            <input
                                                type="tel"
                                                value={contact.phone}
                                                onChange={(e) => handleContactChange('phone', e.target.value)}
                                                className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                placeholder="+46 70 123 4567"
                                                required
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('address')}</label>
                                            <input
                                                type="text"
                                                value={contact.address}
                                                onChange={(e) => handleContactChange('address', e.target.value)}
                                                className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                placeholder="Storgatan 1"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('city')}</label>
                                            <input
                                                type="text"
                                                value={contact.city}
                                                onChange={(e) => handleContactChange('city', e.target.value)}
                                                className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                placeholder="Stockholm"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('zip_code')}</label>
                                            <input
                                                type="text"
                                                value={contact.zipCode}
                                                onChange={(e) => handleContactChange('zipCode', e.target.value)}
                                                className="w-full bg-white border border-gray-300 rounded-xl p-3 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                                placeholder="111 22"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3: Baggage Selection */}
                            {(baggageOptions.length > 0 || baggageLoading) && (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                                        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-900 to-gray-700 text-white flex items-center justify-center text-sm font-bold shadow-sm">3</span>
                                        <h2 className="text-lg font-bold text-gray-900">{t('baggage_selection') || 'Bagaj Seçimi'}</h2>
                                    </div>
                                    <div className="p-6">
                                        {baggageLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                                <span className="ml-3 text-sm text-gray-500">{t('loading') || 'Yükleniyor...'}</span>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {baggageOptions.map((paxOpts, i) => (
                                                    <div key={`${paxOpts.passengerId}-${i}`} className="p-5 bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200/70">
                                                        <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4">
                                                            {t('passenger') || 'Yolcu'} {i + 1}
                                                        </h3>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                            {paxOpts.options.map((opt: any) => {
                                                                const key = `${paxOpts.passengerId}-${paxOpts.direction}`;
                                                                const isSelected = selectedBaggage[key] === opt.offerId;
                                                                return (
                                                                    <button
                                                                        key={opt.offerId}
                                                                        type="button"
                                                                        onClick={() => handleBaggageSelect(paxOpts.passengerId, paxOpts.direction, opt.offerId)}
                                                                        className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${isSelected
                                                                            ? 'border-blue-500 bg-blue-50/80 shadow-md shadow-blue-100 ring-1 ring-blue-200'
                                                                            : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                                                                        }`}
                                                                    >
                                                                        {isSelected && (
                                                                            <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                            </div>
                                                                        )}
                                                                        <div className="flex items-center gap-2 mb-2">
                                                                            <span className="text-xl">🧳</span>
                                                                            <h4 className="font-bold text-gray-900 text-sm">{formatBaggageName(opt.name)}</h4>
                                                                        </div>
                                                                        <div className="pt-2 border-t border-gray-100">
                                                                            {opt.priceAdd === 0 ? (
                                                                                <span className="text-sm font-bold text-green-600">{t('included') || 'Included'}</span>
                                                                            ) : (
                                                                                <span className="text-sm font-bold text-gray-900">
                                                                                    +{Math.round(opt.priceAdd).toLocaleString('sv-SE')} {opt.currency}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Section 4: Branded Fares (if available) */}
                            {(outboundBrandedFares.length > 0 || returnBrandedFares.length > 0 || brandedFaresLoading) && (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                                        <span className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center text-sm font-bold shadow-sm">✈️</span>
                                        <div>
                                            <h2 className="text-lg font-bold text-gray-900">{t('flight_fare_options')}</h2>
                                            <p className="text-xs text-gray-500">{t('choose_fare_type')}</p>
                                        </div>
                                    </div>

                                    <div className="p-6 space-y-6">
                                        {brandedFaresLoading ? (
                                            <div className="flex items-center justify-center py-8">
                                                <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                                                <span className="ml-3 text-sm text-gray-500">{t('loading_fares') || 'Laddar biljettval...'}</span>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Outbound Branded Fares */}
                                                {outboundBrandedFares.length > 0 && (
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <span className="text-sm font-semibold text-gray-700">🛫 {t('outbound_flight') || 'Utresa'}</span>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                            {outboundBrandedFares.map((fare) => (
                                                                <button
                                                                    key={fare.id}
                                                                    type="button"
                                                                    onClick={() => handleFareSelect('outbound', fare.id)}
                                                                    className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${selectedOutboundFare === fare.id
                                                                        ? 'border-blue-500 bg-blue-50/80 shadow-md shadow-blue-100 ring-1 ring-blue-200'
                                                                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                                                                        }`}
                                                                >
                                                                    {selectedOutboundFare === fare.id && (
                                                                        <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                        </div>
                                                                    )}
                                                                    <h4 className="font-bold text-gray-900 text-sm mb-2">{fare.name}</h4>
                                                                    <div className="space-y-1.5 mb-3">
                                                                        {fare.features.slice(0, 5).map((feature, idx) => (
                                                                            <div key={idx} className="flex items-center gap-2">
                                                                                <span className={`material-icons-round text-xs ${feature.included ? 'text-green-500' : 'text-gray-300'}`}>
                                                                                    {feature.included ? feature.icon || 'check_circle' : 'cancel'}
                                                                                </span>
                                                                                <span className={`text-xs ${feature.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                                                                    {feature.name}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                        {fare.features.length > 5 && (
                                                                            <span className="text-xs text-blue-500">+{fare.features.length - 5} {t('more')}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="pt-2 border-t border-gray-100">
                                                                        {fare.price === 0 ? (
                                                                            <span className="text-sm font-bold text-green-600">{t('included')}</span>
                                                                        ) : (
                                                                            <span className="text-sm font-bold text-gray-900">
                                                                                +{Math.round(fare.price).toLocaleString('sv-SE')} {fare.currency}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Return Branded Fares */}
                                                {returnBrandedFares.length > 0 && (
                                                    <div>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <span className="text-sm font-semibold text-gray-700">🛬 {t('return_flight') || 'Hemresa'}</span>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                                            {returnBrandedFares.map((fare) => (
                                                                <button
                                                                    key={fare.id}
                                                                    type="button"
                                                                    onClick={() => handleFareSelect('return', fare.id)}
                                                                    className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${selectedReturnFare === fare.id
                                                                        ? 'border-blue-500 bg-blue-50/80 shadow-md shadow-blue-100 ring-1 ring-blue-200'
                                                                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                                                                        }`}
                                                                >
                                                                    {selectedReturnFare === fare.id && (
                                                                        <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                                                                        </div>
                                                                    )}
                                                                    <h4 className="font-bold text-gray-900 text-sm mb-2">{fare.name}</h4>
                                                                    <div className="space-y-1.5 mb-3">
                                                                        {fare.features.slice(0, 5).map((feature, idx) => (
                                                                            <div key={idx} className="flex items-center gap-2">
                                                                                <span className={`material-icons-round text-xs ${feature.included ? 'text-green-500' : 'text-gray-300'}`}>
                                                                                    {feature.included ? feature.icon || 'check_circle' : 'cancel'}
                                                                                </span>
                                                                                <span className={`text-xs ${feature.included ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                                                                    {feature.name}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                        {fare.features.length > 5 && (
                                                                            <span className="text-xs text-blue-500">+{fare.features.length - 5} {t('more')}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className="pt-2 border-t border-gray-100">
                                                                        {fare.price === 0 ? (
                                                                            <span className="text-sm font-bold text-green-600">{t('included')}</span>
                                                                        ) : (
                                                                            <span className="text-sm font-bold text-gray-900">
                                                                                +{Math.round(fare.price).toLocaleString('sv-SE')} {fare.currency}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* RIGHT: Din resa — Booking Summary Sidebar */}
                    <div className="lg:w-1/3">
                        <div className="sticky top-24 space-y-4">
                            {/* Transaction Error Alert */}
                            {transactionError && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-2xl">
                                    <div className="flex items-center gap-2 text-red-700">
                                        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-medium text-sm">{formatErrorMessage(transactionError)}</span>
                                    </div>
                                    <button
                                        onClick={initializeTransaction}
                                        className="mt-2 text-sm text-red-600 hover:text-red-800 underline font-medium"
                                    >
                                        {t('start_new_session')}
                                    </button>
                                </div>
                            )}

                            {/* Din resa Card */}
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/80 overflow-hidden">
                                {/* Header: Din resa + Timer */}
                                <div className="px-6 pt-6 pb-4">
                                    <h3 className="text-2xl font-extrabold text-gray-900 tracking-tight">{t('your_trip')}</h3>
                                    {sessionId && timeRemaining > 0 && (
                                        <p className={`text-sm mt-1 ${bookingService.isAboutToExpire(timeRemaining) ? 'text-red-500 font-semibold animate-pulse' : 'text-gray-500'}`}>
                                            {t('booking_valid_for')} {bookingService.formatTimeRemaining(timeRemaining)}
                                        </p>
                                    )}
                                    {transactionLoading && (
                                        <div className="flex items-center gap-2 mt-1 text-gray-400 text-sm">
                                            <div className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
                                            {t('loading') || 'Loading...'}
                                        </div>
                                    )}
                                    {/* Dela resan (Share trip) button */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (navigator.share) {
                                                navigator.share({ title: apiHotelName, url: window.location.href });
                                            } else {
                                                navigator.clipboard.writeText(window.location.href);
                                                alert(t('link_copied'));
                                            }
                                        }}
                                        className="mt-3 flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                                        {t('share_trip')}
                                    </button>
                                </div>

                                {/* Hotel Section — Collapsible */}
                                <div className="border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const el = document.getElementById('booking-hotel-section');
                                            if (el) el.classList.toggle('hidden');
                                            const arrow = document.getElementById('hotel-arrow');
                                            if (arrow) arrow.classList.toggle('rotate-180');
                                        }}
                                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                    >
                                        <h4 className="font-bold text-gray-900 text-base">{apiHotelName}</h4>
                                        <svg id="hotel-arrow" className="w-5 h-5 text-gray-400 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                    </button>
                                    <div id="booking-hotel-section" className="px-6 pb-5 space-y-4">
                                        {/* Check-in / Check-out */}
                                        <div className="flex items-start gap-6">
                                            <div className="flex items-start gap-2.5">
                                                <span className="text-gray-400 mt-0.5">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                                </span>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{formatDate(apiCheckIn)}</p>
                                                    <p className="text-xs text-gray-400">{apiCheckInTime}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-2.5">
                                                <span className="text-gray-400 mt-0.5">
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" /></svg>
                                                </span>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{formatDate(apiCheckOut)}</p>
                                                    <p className="text-xs text-gray-400">{apiCheckOutTime}</p>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Room info */}
                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                            <span>
                                                <span className="font-semibold">{t('room')} {searchQuery?.rooms || 1}</span>
                                                <span className="text-gray-400"> - </span>
                                                <span>{apiTravellersInRoom} {t('adults_short')}</span>
                                                {(searchQuery?.children || 0) > 0 && <span>, {searchQuery.children} {t('children_short')}</span>}
                                                <span className="text-gray-400">, </span>
                                                <span>{apiRoomName}</span>
                                                <span className="text-gray-400">, </span>
                                                <span>{apiBoardName}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Flight Section — Collapsible */}
                                {(apiOutbound || apiReturn || offer.outboundFlight || offer.returnFlight) && (
                                    <div className="border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const el = document.getElementById('booking-flight-section');
                                                if (el) el.classList.toggle('hidden');
                                                const arrow = document.getElementById('flight-arrow');
                                                if (arrow) arrow.classList.toggle('rotate-180');
                                            }}
                                            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                                        >
                                            <h4 className="font-bold text-gray-900 text-base">{t('flight')}</h4>
                                            <svg id="flight-arrow" className="w-5 h-5 text-gray-400 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                            </svg>
                                        </button>
                                        <div id="booking-flight-section" className="px-6 pb-5 space-y-4">
                                            {/* Outbound */}
                                            {apiOutbound && (
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
                                                        <p className="text-sm font-bold text-gray-900">
                                                            {apiOutbound.departureTime || '—'}
                                                            <span className="text-gray-400 font-normal mx-2">|</span>
                                                            {formatDate(apiOutbound.flightDate)}
                                                        </p>
                                                    </div>
                                                    <p className="text-sm text-gray-600 ml-6">
                                                        <span className="font-semibold text-teal-700">{apiOutbound.departureAirport}</span>
                                                        <span className="text-gray-400 mx-1.5">-</span>
                                                        <span className="font-semibold text-teal-700">{apiOutbound.arrivalAirport}</span>
                                                    </p>
                                                    <p className="text-xs text-gray-400 ml-6">{apiOutbound.flightNumber}</p>
                                                </div>
                                            )}
                                            {/* Return */}
                                            {apiReturn && (
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                                                        <p className="text-sm font-bold text-gray-900">
                                                            {apiReturn.departureTime || '—'}
                                                            <span className="text-gray-400 font-normal mx-2">|</span>
                                                            {formatDate(apiReturn.flightDate)}
                                                        </p>
                                                    </div>
                                                    <p className="text-sm text-gray-600 ml-6">
                                                        <span className="font-semibold text-teal-700">{apiReturn.departureAirport}</span>
                                                        <span className="text-gray-400 mx-1.5">-</span>
                                                        <span className="font-semibold text-teal-700">{apiReturn.arrivalAirport}</span>
                                                    </p>
                                                    <p className="text-xs text-gray-400 ml-6">{apiReturn.flightNumber}</p>
                                                </div>
                                            )}
                                            {/* Full flight info link */}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const el = document.getElementById('full-flight-details');
                                                    if (el) el.classList.toggle('hidden');
                                                }}
                                                className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer font-medium pt-1 flex items-center gap-1"
                                            >
                                                {t('show_full_flight_info')}
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                            </button>

                                            {/* Full flight segment details (hidden by default) */}
                                            <div id="full-flight-details" className="hidden mt-3 space-y-3 border-t border-gray-100 pt-3">
                                                {flightSegments.length > 0 ? (
                                                    flightSegments.map((seg: any, idx: number) => (
                                                        <div key={idx} className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-200/70">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${seg.route === 1 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                    {seg.route === 1 ? '🛫 ' + (t('outbound') || 'Outbound') : '🛬 ' + (t('return_flight') || 'Return')}
                                                                </span>
                                                                <span className="text-xs font-mono text-gray-500">
                                                                    {seg.flightNumber || seg.flightNo || `${seg.airlineName && !seg.airlineName.toLowerCase().includes('amadeus') ? seg.airlineName : ''} ${seg.flightNumber || ''}`}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between">
                                                                <div className="text-center">
                                                                    <p className="text-lg font-bold text-gray-900">{(seg.departureTime || '').substring(0, 5) || '—'}</p>
                                                                    <p className="text-xs font-semibold text-teal-700">{seg.departureAirport || seg.from?.code || '—'}</p>
                                                                    <p className="text-xs text-gray-400">{seg.departureDate || seg.date || ''}</p>
                                                                </div>
                                                                <div className="flex-1 mx-4 flex flex-col items-center">
                                                                    <div className="w-full h-px bg-gray-300 relative">
                                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2">
                                                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                                                                        </div>
                                                                    </div>
                                                                    {seg.airlineName && !seg.airlineName.toLowerCase().includes('amadeus') && (
                                                                        <p className="text-xs text-gray-400 mt-1">{seg.airlineName}</p>
                                                                    )}
                                                                </div>
                                                                <div className="text-center">
                                                                    <p className="text-lg font-bold text-gray-900">{(seg.arrivalTime || '').substring(0, 5) || '—'}</p>
                                                                    <p className="text-xs font-semibold text-teal-700">{seg.arrivalAirport || seg.to?.code || '—'}</p>
                                                                    <p className="text-xs text-gray-400">{seg.arrivalDate || ''}</p>
                                                                </div>
                                                            </div>
                                                            {/* Baggage info */}
                                                            {(seg.baggage || seg.baggageAllowance) && (
                                                                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                                                    🧳 {seg.baggage || seg.baggageAllowance}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-sm text-gray-400 italic">{t('no_flight_details') || 'Flight details will be available after booking confirmation.'}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Price Section */}
                                <div className="border-t border-gray-200">
                                    <div className="px-6 py-6 text-center">
                                        {/* Per person price */}
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1">{t('per_person')}</p>
                                        <p className="text-4xl font-extrabold text-gray-900 tracking-tight">
                                            {Math.round(pricePerPerson).toLocaleString('sv-SE')} <span className="text-xl font-bold">{apiCurrency === 'EUR' ? '€' : 'kr'}</span>
                                        </p>
                                        {/* Total price */}
                                        <p className="text-sm text-gray-500 mt-1">
                                            <span className="font-bold text-gray-700">{Math.round(apiTotalPrice).toLocaleString('sv-SE')} {apiCurrency === 'EUR' ? '€' : 'kr'}</span> {t('total_amount_label')}
                                        </p>
                                        {priceDelta !== 0 && (
                                            <p className={`text-xs font-semibold mt-1 ${priceDelta > 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                {priceDelta > 0 ? '+' : ''}{Math.round(priceDelta).toLocaleString('sv-SE')} {apiCurrency} {t('flight_change')}
                                            </p>
                                        )}
                                        {baggageDelta !== 0 && (
                                            <p className="text-xs font-semibold mt-1 text-red-500">
                                                +{Math.round(baggageDelta).toLocaleString('sv-SE')} {apiCurrency} {t('extra_baggage') || 'Ek Bagaj'}
                                            </p>
                                        )}

                                        {/* Payment method icons */}
                                        <div className="flex items-center justify-center gap-3 mt-4">
                                            {/* Visa */}
                                            <svg className="h-6" viewBox="0 0 48 16" fill="none"><rect width="48" height="16" rx="2" fill="#1A1F71" /><text x="24" y="12" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial">VISA</text></svg>
                                            {/* Mastercard */}
                                            <svg className="h-6" viewBox="0 0 38 24" fill="none">
                                                <rect width="38" height="24" rx="3" fill="#F5F5F5" stroke="#E0E0E0" strokeWidth="0.5" />
                                                <circle cx="15" cy="12" r="7" fill="#EB001B" opacity="0.9" />
                                                <circle cx="23" cy="12" r="7" fill="#F79E1B" opacity="0.9" />
                                                <path d="M19 6.7a7 7 0 010 10.6 7 7 0 000-10.6z" fill="#FF5F00" />
                                            </svg>
                                            {/* Klarna */}
                                            <span className="text-xs font-extrabold text-pink-600 bg-pink-50 px-2 py-1 rounded">Klarna.</span>
                                            {/* Swish */}
                                            <span className="text-xs font-bold text-white bg-[#59A843] px-2 py-1 rounded flex items-center gap-0.5">
                                                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="white"><circle cx="6" cy="6" r="5" stroke="white" strokeWidth="1.5" fill="none" /></svg>
                                                swish
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit CTA Button */}
                                <div className="px-5 pb-5">
                                    <button
                                        type="submit"
                                        form="bookingForm"
                                        disabled={verifyingPrice || transactionLoading || isSubmitting || !sessionId}
                                        className="w-full font-bold py-4 rounded-xl shadow-lg transition-all transform hover:-translate-y-0.5 hover:shadow-xl active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-white text-base tracking-wide"
                                        style={{
                                            background: (verifyingPrice || transactionLoading || isSubmitting || !sessionId)
                                                ? '#9ca3af'
                                                : 'linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)',
                                            boxShadow: (verifyingPrice || transactionLoading || isSubmitting || !sessionId)
                                                ? 'none'
                                                : '0 4px 20px rgba(13, 148, 136, 0.4)'
                                        }}
                                    >
                                        {transactionLoading ? '⏳ Starting session...' :
                                            isSubmitting ? '⏳ Processing...' :
                                                verifyingPrice ? '⏳ Verifying price...' :
                                                    t('proceed_to_payment')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Price Warning Modal */}
            {priceWarning?.show && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
                        <div className="text-center mb-6">
                            <div className="text-5xl mb-4">
                                {priceWarning.priceIncreased ? '⚠️' : '🎉'}
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                {t('price_change')}
                            </h2>
                            <p className="text-gray-600">
                                {priceWarning.message}
                            </p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-4 mb-6">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-xs text-gray-500">{t('old_price')}</p>
                                    <p className="text-lg font-bold text-gray-400 line-through">
                                        {Math.round(offer.price).toLocaleString('sv-SE')} {offer.currency}
                                    </p>
                                </div>
                                <div className="text-2xl text-gray-300">→</div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-500">{t('new_price')}</p>
                                    <p className={`text-xl font-bold ${priceWarning.priceIncreased ? 'text-red-600' : 'text-green-600'}`}>
                                        {Math.round(priceWarning.currentPrice).toLocaleString('sv-SE')} {offer.currency}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setPriceWarning(null)}
                                className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={() => setPriceWarning(null)}
                                className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                            >
                                {t('continue_new_price')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
